import { useEffect, useRef, useState } from 'react';
import { Button, DatePicker, Input, InputNumber } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import CompanySelect from './CompanySelect';
import PersonSelect from './PersonSelect';
import ProjectSelect from './ProjectSelect';
import AccountSelect from './AccountSelect';
import TagsSelect from './TagsSelect';
import { normalize } from './DebouncedNumberInput';

const SELECTS = {
  company: CompanySelect,
  person: PersonSelect,
  project: ProjectSelect,
  account: AccountSelect,
  tag: TagsSelect,
};

// Clicks landing in these still belong to the editor: its own box, the
// select/date popups antd portals to <body> (including TagsSelect's nested
// "parent tag" picker in its create footer), and the confirmation modal a
// commit may raise -- it decides the edit's fate itself.
const INSIDE = '.ant-select-dropdown, .ant-picker-dropdown, .ant-modal-root';

// Room for the widest value plus ✓ / ✗. Selects and the title fill their
// fixed-width columns instead.
const MIN_WIDTH = { date: 170, money: 170, rate: 130 };

const toDraft = (kind, value) => {
  if (kind === 'date') return value ? dayjs(value) : null;
  if (kind === 'text') return value ?? '';
  return value ?? null;
};

// "122.00" vs 122, "" vs null: compare what would actually be saved.
const sameValue = (kind, a, b) => {
  if (kind === 'money' || kind === 'rate') return a != null && b != null && Number(a) === Number(b);
  if (kind === 'date') return (a ? a.format('YYYY-MM-DD') : null) === (b ? b.format('YYYY-MM-DD') : null);
  return (a ?? null) === (b ?? null);
};

/**
 * One table cell in edit mode: the editor for `kind` plus ✓ / ✗.
 *
 * Enter or ✓ commits, Esc or ✗ cancels, and any click outside discards. Picking
 * from a select or the calendar commits straight away -- the pick is the
 * decision. `onCommit` receives the value in the drawer form's own shape
 * (dayjs for dates, a Number for amounts); an unchanged value just closes.
 */
const InlineEditCell = ({ kind, value, min = 0, saving = false, onCommit, onCancel }) => {
  const initial = useRef(toDraft(kind, value)).current;
  const [draft, setDraft] = useState(initial);
  const boxRef = useRef(null);

  // The listener outlives renders; read the latest callbacks through refs.
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;
  const savingRef = useRef(saving);
  savingRef.current = saving;

  useEffect(() => {
    const onDown = (e) => {
      if (savingRef.current) return;
      if (boxRef.current?.contains(e.target)) return;
      if (e.target.closest?.(INSIDE)) return;
      cancelRef.current();
    };
    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, []);

  const valid = kind === 'text'
    ? draft.trim() !== ''             // the drawer requires a title
    : kind === 'money' || kind === 'rate'
      ? draft != null && draft !== '' && !isNaN(Number(normalize(draft)))
      : kind === 'date' ? draft != null : true;

  const commit = (next = draft) => {
    if (saving) return;
    if (sameValue(kind, next, initial)) { onCancel(); return; }
    if (kind === 'text') onCommit(next.trim());
    else if (kind === 'money' || kind === 'rate') onCommit(Number(normalize(next)));
    else onCommit(next ?? null);
  };

  const submit = () => { if (valid) commit(); };

  // ✓ / ✗ must not take focus: a blur would read as clicking away.
  const keepFocus = (e) => e.preventDefault();

  let editor;
  if (kind === 'text') {
    editor = (
      <Input size="small" autoFocus value={draft} disabled={saving}
             onChange={e => setDraft(e.target.value)} onPressEnter={submit} />
    );
  } else if (kind === 'money' || kind === 'rate') {
    editor = (
      <InputNumber size="small" autoFocus stringMode precision={2} step={0.01}
                   min={min} parser={normalize} value={draft} disabled={saving}
                   addonAfter={kind === 'rate' ? '%' : '€'} style={{ width: '100%' }}
                   onChange={setDraft} onPressEnter={submit} />
    );
  } else if (kind === 'date') {
    editor = (
      <DatePicker size="small" autoFocus defaultOpen format="D. M. YYYY" value={draft}
                  allowClear={false} disabled={saving} style={{ width: '100%' }}
                  onChange={d => { setDraft(d); if (d) commit(d); }} />
    );
  } else {
    const Select = SELECTS[kind];
    editor = (
      <Select size="small" autoFocus defaultOpen minWidth={0} value={draft}
              popupMatchSelectWidth={false} autoPreselect={false}
              onChange={v => { setDraft(v ?? null); commit(v ?? null); }} />
    );
  }

  return (
    <div
      ref={boxRef}
      className="inline-edit-cell"
      style={{ minWidth: MIN_WIDTH[kind] }}
      // Selects and the date picker handle Enter themselves (a pick commits);
      // only Esc is ours everywhere. Portal events bubble here too.
      onKeyDown={e => { if (e.key === 'Escape' && !saving) { e.stopPropagation(); onCancel(); } }}
      onDoubleClick={e => e.stopPropagation()}
    >
      <div className="inline-edit-cell__editor">{editor}</div>
      <Button size="small" icon={<CheckOutlined />} loading={saving}
              disabled={!valid} onMouseDown={keepFocus} onClick={submit} />
      <Button size="small" icon={<CloseOutlined />} disabled={saving}
              onMouseDown={keepFocus} onClick={onCancel} />
    </div>
  );
};

export default InlineEditCell;
